import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { LOGOUT } from '../../constants/actions/auth.constants';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import IAdminCustomer from '../types/IAdminCustomer';
import AdminCustomerService from '../services/AdminCustomerService';
import IPaginationState from '../../types/state/IPaginationState';
import IAdminUser from '../types/users/IAdminUser';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';

const NAME = 'AdminCustomer';

const getCustomer = createNotifiedThunk(
    `${NAME}/getCustomer`,
    AdminCustomerService.getCustomer,
    { error: 'fe.admin.error.load-company' },
);
const updateCustomer = createNotifiedThunk(
    `${NAME}/updateCustomer`,
    AdminCustomerService.updateCustomer,
    { error: 'fe.admin.error.load-company' },
);
const getCustomers = createNotifiedThunk(
    `${NAME}/getCustomers`,
    AdminCustomerService.getCustomers,
    { error: 'fe.admin.error.load-companies' },
);
const createCustomer = createNotifiedThunk(
    `${NAME}/createCustomer`,
    AdminCustomerService.createCustomer,
    { error: 'fe.admin.error.create-company' },
);
const getCompanyAdmins = createNotifiedThunk(
    `${NAME}/getCompanyAdmins`,
    AdminCustomerService.getCompanyAdmins,
    { error: 'fe.admin.error.load-users' },
);
const createCompanyAdmin = createNotifiedThunk(
    `${NAME}/createCompanyAdmin`,
    AdminCustomerService.createCompanyAdmin,
    { error: 'fe.admin.error.create-user' },
);

export type AdminCustomerState = {
    list: IPaginationState<IAdminCustomer[]>;
    detail: IBaseState<IAdminCustomer | null>;
    admins: IBaseState<IAdminUser[]>;
};

const initialState: AdminCustomerState = {
    list: {
        data: [],
        loading: false,
        pagination: { page: 1 },
    },
    detail: {
        data: null,
        loading: false,
    },
    admins: {
        data: [],
        loading: false,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getCustomer.pending, (state: AdminCustomerState) => {
            state.detail.success = false;
            state.detail.error = false;
            state.detail.loading = true;
        });
        builder.addCase(
            getCustomer.fulfilled,
            (state: AdminCustomerState, action) => {
                state.detail.data = action.payload.data;
                state.detail.success = true;
            },
        );
        builder.addCase(getCustomer.rejected, (state: AdminCustomerState) => {
            state.detail.error = true;
        });

        builder.addCase(getCustomers.pending, (state: AdminCustomerState) => {
            state.list.loading = true;
            state.list.data = [];
        });
        builder.addCase(
            getCustomers.fulfilled,
            (state: AdminCustomerState, action) => {
                state.list.data = action.payload.data;
                if ((action.payload as any).pagination) {
                    state.list.pagination = (action.payload as any).pagination;
                }
            },
        );

        builder.addCase(
            getCompanyAdmins.pending,
            (state: AdminCustomerState) => {
                state.admins.data = [];
                state.admins.loading = true;
            },
        );
        builder.addCase(
            getCompanyAdmins.fulfilled,
            (state: AdminCustomerState, action) => {
                state.admins.data = action.payload.data;
            },
        );
        builder.addCase(
            createCompanyAdmin.fulfilled,
            (state: AdminCustomerState, action) => {
                state.admins.data.push(action.payload.data);
            },
        );

        builder.addCase(LOGOUT, () => initialState);

        builder.addMatcher(
            isAnyOf(getCustomer.fulfilled, getCustomer.rejected),
            (state: AdminCustomerState) => {
                state.detail.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getCompanyAdmins.fulfilled, getCompanyAdmins.rejected),
            (state: AdminCustomerState) => {
                state.admins.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getCustomers.fulfilled, getCustomers.rejected),
            (state: AdminCustomerState) => {
                state.list.loading = false;
            },
        );
    },
});

const state = (state: AppState) => state.admin.customer;

const all = state;

const AdminCustomerSlice = {
    reducer: slice.reducer,
    actions: {
        getCustomer,
        updateCustomer,
        getCustomers,
        createCustomer,
        getCompanyAdmins,
        createCompanyAdmin,
    },
    state: {
        all,
        detail: createSelector(
            all,
            (state: AdminCustomerState) => state.detail,
        ),
        list: createSelector(all, (state: AdminCustomerState) => state.list),
        admins: createSelector(
            all,
            (state: AdminCustomerState) => state.admins,
        ),
    },
};

export default AdminCustomerSlice;
